import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import logger from '../../utils/logger';
import { scopeLabel } from '../../utils/catalogue';

/**
 * REQ-02 — moderators are opted in to emails by default (daily digest) and
 * can change or opt out here without an administrator.
 */
const NotificationPreferences = ({ uid, profile, role }) => {
  const prefs = profile?.notificationPrefs || {};
  const email = prefs.email !== false;
  const frequency = prefs.frequency === 'immediate' ? 'immediate' : 'digest';
  const [status, setStatus] = useState('');

  const save = async (next) => {
    setStatus('Saving…');
    try {
      await updateDoc(doc(db, 'users', uid), { notificationPrefs: { email, frequency, ...next } });
      setStatus('Saved');
      setTimeout(() => setStatus(''), 2000);
    } catch (err) {
      logger.error('Error saving notification preferences:', err);
      setStatus('Could not save. Please try again.');
    }
  };

  const scopes = profile?.moderationScopes || [];

  return (
    <div className="mt-8">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3 px-0.5">Moderation notifications</p>
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600">
            {role === 'admin'
              ? 'As an administrator you are notified about uploads into subjects and grades that have no moderator.'
              : 'You are notified about uploads into your moderation scopes.'}
          </p>
          {role !== 'admin' && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {scopes.length === 0 && <span className="text-xs text-amber-700">No scopes assigned yet.</span>}
              {scopes.map((s) => (
                <span key={s} className="rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700">{scopeLabel(s)}</span>
              ))}
            </div>
          )}
        </div>

        <label className="px-6 py-4 flex items-center justify-between gap-4 cursor-pointer">
          <span>
            <span className="block text-sm font-medium text-gray-900">Email notifications</span>
            <span className="block text-xs text-gray-500">New-upload alerts are always shown on the site.</span>
          </span>
          <input
            type="checkbox"
            className="h-5 w-5 accent-brand-600"
            checked={email}
            onChange={(e) => save({ email: e.target.checked })}
          />
        </label>

        <fieldset className={`px-6 py-4 ${email ? '' : 'opacity-50'}`} disabled={!email}>
          <legend className="sr-only">Email delivery</legend>
          <p className="text-sm font-medium text-gray-900 mb-2">Email delivery</p>
          <label className="flex items-start gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
            <input type="radio" name="frequency" className="mt-0.5 accent-brand-600" checked={frequency === 'digest'} onChange={() => save({ frequency: 'digest' })} />
            <span>
              Daily digest <span className="text-gray-400">(recommended)</span>
              <span className="block text-xs text-gray-500">One email each morning listing resources still waiting.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="radio" name="frequency" className="mt-0.5 accent-brand-600" checked={frequency === 'immediate'} onChange={() => save({ frequency: 'immediate' })} />
            <span>
              Immediately
              <span className="block text-xs text-gray-500">An email per upload; bulk uploads are combined into one email.</span>
            </span>
          </label>
        </fieldset>
        {status && <p className="px-6 py-2 text-xs text-gray-500">{status}</p>}
      </div>
    </div>
  );
};

export default NotificationPreferences;
